import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {
    Box,
    Button,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Typography,
} from "@mui/material";
import {useEffect, useMemo, useState} from "react";
import {Check, Plus, X} from "lucide-react";
import {fetchCategories} from "../../api/api";
import CreateCategoryDialog from "./CreateCategoryDialog";

export default function CategoryDialog({rows, open, closeCategoryDialog, onSelectCategory, submitSaveAd}) {
    const [categories, setCategories] = useState(Array.isArray(rows) ? rows : [])
    const [isLoading, setIsLoading] = useState(false)
    const [errorMsg, setErrorMsg] = useState(null)
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [openCreate, setOpenCreate] = useState(false)

    const selectedCode = selectedCategory?.FK_Category_Code

    useEffect(() => {
        if (Array.isArray(rows) && rows.length) {
            setCategories(rows)
        }
    }, [rows])

    const loadCategories = async () => {
        setIsLoading(true)
        setErrorMsg(null)
        try {
            const data = await fetchCategories()
            setCategories(Array.isArray(data) ? data : [])
        } catch (e) {
            console.error(e)
            setErrorMsg('No se pudieron cargar las categorías.')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (open && !openCreate) {
            loadCategories()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, openCreate])

    const handleSelectCategory = (category) => {
        setSelectedCategory(category)
        if (onSelectCategory) {
            onSelectCategory(category?.FK_Category_Code)
        }
    }

    const canSave = useMemo(() => Boolean(selectedCode), [selectedCode])

    return (
        <>
            <Dialog
                open={Boolean(open) && !openCreate}
                onClose={closeCategoryDialog}
                fullWidth
                maxWidth="xs"
                BackdropProps={{
                    sx: {
                        backdropFilter: 'blur(6px)',
                        backgroundColor: 'rgba(0,0,0,0.35)',
                    },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: 24,
                        bgcolor: 'white',
                    },
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2}}>
                    <Typography variant="h6" sx={{fontWeight: 700, color: 'black'}}>
                       Escoje una Categoría
                    </Typography>
                    <IconButton aria-label="Close" onClick={closeCategoryDialog} size="small">
                        <X size={18} />
                    </IconButton>
                </Box>
                <Divider sx={{mt: 2}} />

                <DialogContent sx={{px: 0, py: 0}}>
                    <Box sx={{minWidth: 320, maxHeight: 320, overflowY: 'auto'}}>
                        {isLoading && (
                            <Typography sx={{px: 3, py: 2, color: 'black'}}>
                                Cargando...
                            </Typography>
                        )}
                        {!isLoading && errorMsg && (
                            <Typography sx={{px: 3, py: 2, color: 'black'}}>
                                {errorMsg}
                            </Typography>
                        )}

                        {!isLoading && !errorMsg && (
                            <List disablePadding>
                                {categories.map((category, idx) => {
                                    const isSelected = selectedCode && selectedCode === category.FK_Category_Code
                                    return (
                                        <Box key={category.id ?? `${category.FK_Category_Code}-${idx}`}>
                                            <ListItemButton
                                                onClick={() => handleSelectCategory(category)}
                                                sx={{
                                                    px: 3,
                                                    py: 1.25,
                                                    bgcolor: isSelected ? 'rgba(25, 118, 210, 0.10)' : 'transparent',
                                                    '&:hover': {
                                                        bgcolor: isSelected
                                                            ? 'rgba(25, 118, 210, 0.12)'
                                                            : 'rgba(0,0,0,0.04)',
                                                    },
                                                }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography sx={{color: 'black'}}>
                                                            {category.Category_Name}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography variant="caption" sx={{color: 'rgba(0,0,0,0.65)'}}>
                                                            {category.FK_Category_Code}
                                                        </Typography>
                                                    }
                                                />
                                                {isSelected && <Check size={18} />}
                                            </ListItemButton>
                                            {idx < categories.length - 1 && <Divider />}
                                        </Box>
                                    )
                                })}
                            </List>
                        )}
                    </Box>
                </DialogContent>

                <Divider />

                <DialogActions sx={{px: 3, py: 2, display: 'flex', justifyContent: 'space-between'}}>
                    <Button
                        variant="text"
                        onClick={() => setOpenCreate(true)}
                        sx={{textTransform: 'none', color: 'black'}}
                        startIcon={<Plus size={16} />}
                    >
                        Crear Categoría
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitSaveAd}
                        disabled={!canSave}
                        sx={{textTransform: 'none'}}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            <CreateCategoryDialog
                open={openCreate}
                onClose={() => setOpenCreate(false)}
                onCreated={async () => {
                    setOpenCreate(false)
                    await loadCategories()
                }}
            />
        </>
    );
}